/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import ReactDOM, { flushSync } from 'react-dom'
import { Grid, message as $message, Modal } from 'antd'
import { PAGESIZE_CG_KEY } from '@/modules/globals/constants/tablePageSizeKey'
import useGetListExtraData from '@/modules/globals/hooks/useGetListExtraData'
import { isNumber } from '@/utils/utils'
import { storage } from '@/hooks/useIknow/index'
import { initialState, reducer } from './reducer'
import { useMemoizedFn } from 'ahooks'

const { useBreakpoint } = Grid

const matchColumn = (matches, layout) => {
    const breakPoint = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'].find(v => matches[v])

    if (breakPoint) {
        return layout[breakPoint]
    }

    return null
}

const setLogPageSize = (name, pageSize) => {
    let pageKeys = storage.get(PAGESIZE_CG_KEY) || {}
    pageKeys[name] = pageSize
    storage.set(PAGESIZE_CG_KEY, pageKeys)
}

const TRIGGER_ENUM = {
    pagination: 'pagination'
}

// linkMode：浏览器地址控制分页时使用
const useListFetch = (fetch, config) => {
    const {
        autoInitLoad = true, // 自动初始加载
        enableLoadControl, //  是否启用加载控制（开启后fetch变更不会自动触发分页信息重置且搜索）
        logPageSizeKey,
        logKey,
        transData,
        grid,
        rowKey,
        pageSizeOptions: pageSizeOpts,
        showSizeChanger = true,
        showQuickJumper = true,
        paginationGrid,
        linkMode = false,
        pageNum,
        showTotal,
        pageSize,
        repeatCancel = true,
        onChange,
        beginLoading = null, // 开始loading的时间 默认立即
        pageChangeScrollTopConfig, // 滚动到顶部配置
        onLoadingData,
        onInitLoadData, // 初次数据加载
        defaultPageSize, // 默认单页显示条数，不传则取pageSizeOptions[0]
        repeatLock, // 打开请求锁
        scrollToTop, // 自定义滚动方法
        extraFetchMap, // 额外数据配置
        ...rest
    } = config || {}

    const matches = useBreakpoint()
    const $fetchIdRef = useRef(0)
    const $lock = useRef(false)
    const firstLoadRef = useRef(false)

    const column = useMemo(() => {
        if (!grid) {
            return 0
        }

        return matchColumn(matches, grid)
    }, [matches])

    // tip 外部的 grid, column, pageSizeOpts, paginationGrid 通过组件传入对象的话，会导致接口的调用
    const pageSizeOptions = useMemo(() => {
        if (!grid) {
            return pageSizeOpts || [20, 30, 40, 50, 100, 300]
        }

        if (!column) {
            return []
        }

        return (paginationGrid || [7, 9, 10, 12, 14]).map(v => v * column).map(v => String(v))
    }, [grid, column, pageSizeOpts, paginationGrid])

    const logPageSize = useMemo(() => {
        if (!logPageSizeKey) {
            return null
        }
        const pageKeys = storage.get(PAGESIZE_CG_KEY) || {}
        return pageKeys[logPageSizeKey]
    }, [logPageSizeKey])

    const [state, dispatchState] = useReducer(
        rest.reducer ? rest.reducer : reducer,
        rest.initialState
            ? rest.initialState
            : {
                  ...initialState,
                  pageNum: pageNum || initialState.pageNum,
                  pageSize: logPageSize || pageSize || initialState.pageSize,
                  ...rest
              }
    )

    // 额外数据加载完成回调
    const onExtraLoadedData = useMemoizedFn(({ fetchId, gerMergedList }) => {
        const { mergedList, hasItemUpdate } = gerMergedList({ overrideBaseList: true })

        // 有更新才变更列表数据
        const shouldUpdate = hasItemUpdate && fetchId === $fetchIdRef.current

        if (shouldUpdate) {
            dispatchState({
                type: 'UPDATE',
                payload: {
                    data: mergedList
                }
            })
        }
    })

    // 获取列表额外数据
    const extraStore = useGetListExtraData({
        rowKey,
        fetchMap: extraFetchMap,
        onLoadedData: onExtraLoadedData
    })

    const $this = useRef({
        isUnmount: false
    })

    Object.assign($this.current, {
        pageNum: state.pageNum,
        pageSize: state.pageSize
    })

    useEffect(() => {
        return () => {
            $this.current.isUnmount = true
        }
    }, [])

    // 查询总数
    const loadTotal = useMemoizedFn(async ({ fetchId, params }) => {
        if (typeof fetch.getFetchParams !== 'function') {
            return
        }

        const fullParams = fetch.getFetchParams(params)

        // 总数需要额外查询
        if (fullParams.searchType === 1) {
            const { success, message, total } = await fetch({
                ...params,
                searchType: 2
            })

            // 重复请求的时候前一次的返回值不接收
            if (repeatCancel && fetchId !== $fetchIdRef.current) {
                return
            }

            if (success) {
                dispatchState({
                    type: 'UPDATE',
                    payload: {
                        total
                    }
                })
            } else {
                $message.error(message)
            }
        }
    })

    // 加载
    const load = useCallback(
        async ({ pageNum, pageSize, extraData, trigger, ...rest }) => {
            let timer = null

            if ($lock.current & repeatLock) return

            $lock.current = true

            if (isNumber(beginLoading)) {
                timer = setTimeout(() => {
                    dispatchState({
                        type: 'UPDATE',
                        payload: {
                            loading: true
                        }
                    })
                }, beginLoading)
            } else {
                dispatchState({
                    type: 'UPDATE',
                    payload: {
                        loading: true
                    }
                })
            }

            if (repeatCancel) {
                $fetchIdRef.current += 1
            }
            const fetchId = $fetchIdRef.current

            const params = {
                pageNum,
                pageSize,
                ...rest
            }

            if (extraData) {
                params.extraData = extraData
            }

            const shouldRunFirstLoad = (...args) => {
                if (!firstLoadRef.current) {
                    firstLoadRef.current = true

                    onInitLoadData && onInitLoadData(...args)
                }
            }

            try {
                const fullParams = typeof fetch.getFetchParams === 'function' ? fetch.getFetchParams(params) : params

                // 注意：allCount 默认没有值，需要用户自己传。场景：没有查询条件时的总数，空状态展示
                const { success, message, data, total, code, extra, msg, allCount } = await fetch(params)

                ReactDOM.unstable_batchedUpdates(() => {
                    // 直接清理loading, 防止万一, 解决loading不消失问题
                    timer && clearTimeout(timer)
                    dispatchState({
                        type: 'UPDATE',
                        payload: {
                            loading: false
                        }
                    })

                    // 重复请求的时候前一次的返回值不接收
                    if (repeatCancel && fetchId !== $fetchIdRef.current) {
                        $lock.current = false
                        return
                    }

                    if ($this.current.isUnmount) {
                        $lock.current = false
                        return
                    }

                    logPageSizeKey && setLogPageSize(logPageSizeKey, pageSize)

                    if (success) {
                        const safeData = (data || []).map((item, index) => ({
                            ...item,
                            index: (pageNum - 1) * pageSize + index + 1
                        }))

                        const newData = typeof transData === 'function' ? transData(safeData, params) : safeData

                        Object.assign($this.current, {
                            pageNum,
                            pageSize
                        })

                        // 是否需要加载加载总数
                        const needExtraLoadTotal = fullParams.searchType === 1

                        if (needExtraLoadTotal) {
                            dispatchState({
                                type: 'UPDATE',
                                payload: {
                                    data: newData,
                                    extra: extra || {},
                                    code,
                                    loading: false,
                                    pageNum,
                                    pageSize,
                                    allCount
                                }
                            })

                            if (trigger !== TRIGGER_ENUM.pagination) {
                                loadTotal({
                                    params: fullParams,
                                    fetchId
                                })
                            }
                        } else {
                            dispatchState({
                                type: 'UPDATE',
                                payload: {
                                    data: newData,
                                    total,
                                    extra: extra || {},
                                    code,
                                    loading: false,
                                    pageNum,
                                    pageSize,
                                    allCount
                                }
                            })
                        }

                        // 判断请求数据是否完成
                        onLoadingData && onLoadingData(newData, params)

                        extraStore.loadExtraData({ fetchId, baseList: newData, params })

                        shouldRunFirstLoad(newData, params)
                    } else {
                        if (code === '20221103') {
                            // 只看有货 缓存过期，刷新页面
                            Modal.info({
                                icon: null,
                                title: '提示',
                                okText: '确定',
                                content: '当前页面数据已过期，需要刷新后才能继续操作',
                                onOk: () => {
                                    refresh()
                                }
                            })
                        }

                        // 1020 用于快手pagesize最大只能20，设置大时候，提示错误，不清空数据
                        // 20221102 只看有货 下一页没有数据了 不清空当前数据
                        if (code !== '1020' && code !== '20221102') {
                            Object.assign($this.current, {
                                pageNum,
                                pageSize
                            })

                            dispatchState({
                                type: 'UPDATE',
                                payload: {
                                    data: [],
                                    total: 0,
                                    extra: extra || {},
                                    code,
                                    loading: false,
                                    pageNum,
                                    pageSize,
                                    allCount
                                }
                            })
                            // 判断请求数据是否完成
                            onLoadingData && onLoadingData([], params)

                            extraStore.loadExtraData({ fetchId, baseList: [], params })

                            shouldRunFirstLoad([], params)

                            $message.error(message || msg)
                        } else {
                            // 判断请求数据是否完成
                            onLoadingData && onLoadingData([], params)

                            extraStore.loadExtraData({ fetchId, baseList: [], params })

                            shouldRunFirstLoad([], params)

                            $message.error(message || msg)
                        }
                    }
                })
            } catch (e) {
                console.error('listFetch', e)

                ReactDOM.unstable_batchedUpdates(() => {
                    timer && clearTimeout(timer)
                    dispatchState({
                        type: 'UPDATE',
                        payload: {
                            loading: false
                        }
                    })
                    // 判断请求数据是否完成
                    onLoadingData && onLoadingData(null, params)

                    extraStore.loadExtraData({ fetchId, baseList: null, params })

                    shouldRunFirstLoad(null, params)
                })
            }

            $lock.current = false
        },
        [fetch, transData, repeatCancel, logPageSizeKey]
    )

    // 重载
    const reload = useCallback(
        async query => {
            const targetSize =
                pageSizeOptions.find(v => Number(v) === Number($this.current.pageSize)) ||
                defaultPageSize ||
                pageSizeOptions[0]

            if (!targetSize) {
                return
            }

            const pageSize = Number(targetSize)

            const params = {
                pageNum: 1,
                pageSize,
                ...(query || {}) // 如果启用了加载控制，需要在调用reload时自行传递除分页信息外的查询参数
            }

            if (linkMode) {
                onChange && onChange(params)
            } else {
                await load(params)
            }
        },
        [pageSizeOptions, load]
    )

    // 刷新
    const refresh = useCallback(async () => {
        const { pageNum, pageSize } = $this.current

        const params = {
            pageNum,
            pageSize,
            pageType: undefined
        }

        await load(params)
    }, [load])

    // 初次加载
    const init = useMemoizedFn(query => {
        return reload(query)
    })

    // 重置
    const reset = useMemoizedFn(query => {
        return reload(query)
    })

    // 搜索
    const search = useMemoizedFn(query => {
        return reload(query)
    })

    const updateItem = useMemoizedFn(payload => {
        dispatchState({
            type: 'UPDATE_ITEM',
            payload,
            rowKey
        })
    })

    /** 优化新增项 payload 类型 row[] */
    const addItems = payload => {
        dispatchState({
            type: 'ADD_ITEMS',
            payload
        })
    }

    useEffect(() => {
        if (enableLoadControl) {
            return
        }

        if (!linkMode) {
            return
        }

        const targetSize = pageSizeOptions.find(v => Number(v) === Number($this.current.pageSize)) || pageSizeOptions[0]

        if (!targetSize) {
            return
        }

        const params = {
            pageNum: pageNum || 1,
            pageSize: Number(targetSize)
        }

        dispatchState({
            type: 'UPDATE',
            payload: params
        })

        Object.assign($this.current, params)

        load(params)
    }, [pageSizeOptions, load, enableLoadControl])

    const initLoadRef = useRef(false)

    useEffect(() => {
        if (enableLoadControl) {
            return
        }

        if (linkMode) {
            return
        }

        if (autoInitLoad) {
            reload()
        } else {
            // 第一次不自动加载
            if (initLoadRef.current) {
                reload()
            } else {
                initLoadRef.current = true
            }
        }
    }, [reload, autoInitLoad, enableLoadControl])

    const pageScroll = () => {
        if (typeof scrollToTop === 'function') {
            scrollToTop()
            return
        }

        if (pageChangeScrollTopConfig) {
            try {
                let realScrollTop = 0
                if (pageChangeScrollTopConfig?.containerId) {
                    // 局部滚动
                    if (pageChangeScrollTopConfig.scrollView) {
                        document.querySelector(`${pageChangeScrollTopConfig?.containerId}`).scrollTop = 0
                        return
                    }

                    const containerTop =
                        document.querySelector(`${pageChangeScrollTopConfig?.containerId}`).offsetTop || 0

                    const extraElTop = pageChangeScrollTopConfig?.extraElTop || 0
                    realScrollTop = containerTop - extraElTop
                }
                document.querySelector('#real-root').scrollTop = realScrollTop
            } catch (error) {
                console.error('catchError', error)
            }
        }
    }

    const handleChange = (pageNum, pageSize, extraData) => {
        // extraData 页码变化时 可以传入额外的参数，分销资金明细-我的分销商，上一页/下一页 后端要传额外参数
        const params = {
            pageNum,
            pageSize
        }

        if (linkMode) {
            dispatchState({
                type: 'UPDATE',
                payload: params
            })

            Object.assign($this.current, params)

            onChange && onChange(params)
            pageScroll()
        } else {
            load({ ...params, extraData, trigger: TRIGGER_ENUM.pagination })
            pageScroll()
        }
    }

    // 修改页码
    const onChangePageSize = pageSize => {
        dispatchState({
            type: 'UPDATE',
            payload: {
                pageSize: pageSize
            }
        })
        $this.current.pageSize = pageSize
    }

    return {
        state,
        dispatchState,
        init, // 个人使用，后续扩展
        reset, // 个人使用，后续扩展
        search, // 个人使用，后续扩展
        refresh,
        reload,
        total: state.total,
        dataSource: state.data,
        loading: state.loading,
        allCount: state.allCount,
        pagination: {
            size: 'small',
            showSizeChanger,
            showQuickJumper,
            current: state.pageNum,
            pageSize: state.pageSize,
            total: state.total,
            pageSizeOptions,
            onChange: handleChange,
            showTotal,
            onChangePageSize
        },
        extra: state.extra,
        code: state.code,
        updateItem,
        addItems,
        ...extraStore
    }
}

export default useListFetch
